import { StandardEvents } from '@shopify/events';

const KCAL_PER_GRAM = 3.236;

class CgWizardIpocaloricoEnergiaEngine extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.wizard = this.closest('cg-feeding-wizard');
    if (!this.wizard || this.wizard.dataset.calculatorType !== 'ipocalorico_energia') return;

    this.currentWeightInput = this.wizard.querySelector('[data-ipocalorico-energia-current-weight]');
    this.targetWeightInput = this.wizard.querySelector('[data-ipocalorico-energia-target-weight]');
    this.variants = this.parseVariants();
    this.syncVariant(this.dataset.selectedVariantId || this.wizard.dataset.selectedVariantId);
    this.currentWeightInput?.addEventListener('input', this.validateWeightRelationship, { signal });
    this.targetWeightInput?.addEventListener('input', this.validateWeightRelationship, { signal });
    this.wizard.addEventListener('cg:wizard:calculate', this.handleCalculate, { signal });
    document.addEventListener(StandardEvents.productSelect, this.handleProductSelect, { signal });
    this.validateWeightRelationship();
  }

  disconnectedCallback() { this.controller?.abort(); }

  parseVariants() {
    const script = this.wizard.querySelector('[data-wizard-variants]');
    if (!script?.textContent) return new Map();
    try {
      const variants = JSON.parse(script.textContent);
      return Array.isArray(variants) ? new Map(variants.map((variant) => [String(variant.id), variant])) : new Map();
    } catch (error) {
      console.error('[CG Wizard Ipocalorico Energia] Impossibile leggere le varianti.', error);
      return new Map();
    }
  }

  validateWeightRelationship = () => {
    if (!this.currentWeightInput || !this.targetWeightInput) return;
    const current = this.number(this.currentWeightInput.value);
    const target = this.number(this.targetWeightInput.value);
    this.targetWeightInput.setCustomValidity(current && target && target > current
      ? 'Il peso obiettivo non può essere superiore al peso attuale.'
      : '');
    this.wizard.markResultStale();
    this.wizard.syncControls();
  };

  syncVariant(variantId) {
    this.variant = variantId == null || variantId === '' ? undefined : this.variants.get(String(variantId));
  }

  handleCalculate = (event) => {
    if (event.target !== this.wizard || event.detail?.calculatorType !== 'ipocalorico_energia') return;
    this.lastValues = event.detail.values;
    this.render(this.lastValues);
  };

  render(values) {
    const currentWeight = this.number(values?.current_weight);
    const targetWeight = this.number(values?.target_weight);
    if (currentWeight < 1 || targetWeight < 1 || currentWeight > 80 || targetWeight > 80 || targetWeight > currentWeight) {
      this.wizard.showResult({ note: 'Dati non validi per il calcolo energetico.' });
      return;
    }

    const energyExact = 70 * Math.pow(targetWeight, 0.75);
    const doseExact = energyExact / KCAL_PER_GRAM;
    const packageWeight = this.number(this.variant?.package_weight_g);
    const priceCents = this.number(this.variant?.price);
    let duration;
    let cost;
    let note = targetWeight === currentWeight
      ? 'Il peso obiettivo coincide con il peso attuale: il risultato non rappresenta una riduzione di peso.'
      : undefined;
    if (packageWeight) {
      const daysExact = packageWeight / doseExact;
      if (Number.isFinite(daysExact) && daysExact > 0) {
        duration = `Il sacco da ${this.formatNumber(packageWeight / 1000, 2)} kg dura circa ${Math.max(1, Math.round(daysExact))} giorni di prodotto`;
        const dailyCost = priceCents > 0 ? (priceCents / 100) / daysExact : 0;
        if (Number.isFinite(dailyCost) && dailyCost > 0) cost = `circa ${this.formatCurrency(dailyCost)} al giorno`;
      }
    } else {
      const formatNote = 'Formato selezionato senza peso confezione disponibile.';
      note = note ? `${note} ${formatNote}` : formatNote;
    }

    this.wizard.showResult({
      dose: `${this.formatNumber(Math.round(doseExact))} g / giorno`,
      energy: `${this.formatNumber(Math.round(energyExact))} kcal / giorno`,
      duration,
      cost,
      note,
    });
    this.wizard.dataset.resultStale = 'false';
  }

  handleProductSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;
    const immediateVariantId = event.detail?.variantId;
    if (immediateVariantId != null && this.variants.has(String(immediateVariantId))) this.updateVariant(immediateVariantId);
    if (!event.promise || typeof event.promise.then !== 'function') return;
    event.promise.then(({ detail }) => {
      if (detail?.productId != null && String(detail.productId) !== String(this.dataset.productId)) return;
      this.updateVariant(detail?.resource?.id ?? detail?.variant?.id ?? detail?.id);
    }).catch(() => {});
  };

  updateVariant(variantId) {
    this.syncVariant(variantId);
    if (variantId != null) {
      this.dataset.selectedVariantId = String(variantId);
      this.wizard.dataset.selectedVariantId = String(variantId);
    }
    const result = this.wizard.querySelector('[data-wizard-result]');
    if (this.lastValues && result && !result.hidden) this.render(this.lastValues);
  }

  number(value) {
    if (value == null || value === '') return 0;
    const parsed = Number(typeof value === 'string' ? value.replace(',', '.').trim() : value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  formatNumber(value, maximumFractionDigits = 0) {
    try { return new Intl.NumberFormat(document.documentElement.lang || undefined, { maximumFractionDigits }).format(value); }
    catch { return String(value); }
  }

  formatCurrency(value) {
    try { return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(value); }
    catch { return `€${Number(value).toFixed(2)}`; }
  }
}

if (!customElements.get('cg-wizard-ipocalorico-energia-engine')) {
  customElements.define('cg-wizard-ipocalorico-energia-engine', CgWizardIpocaloricoEnergiaEngine);
}
